import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Modal, Form, Table } from "react-bootstrap";
import { FaCopy } from "react-icons/fa";
import axios from "axios";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import { toast } from "react-hot-toast";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import { Can } from "../../../authentication/AuthContext";

export default function ResellerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [screenloader, setScreenloader] = useState(true);
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const TX_LIMIT = 10;

  // assign-credits modal
  const [showModal, setShowModal] = useState(false);
  const [credits, setCredits] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchDetail = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/resellers/${id}`,
        {
          headers: { "access-token": localStorage.getItem("accessToken") },
          params: { page: txPage, limit: TX_LIMIT },
        }
      );
      setScreenloader(false);
      if (res.data.status) {
        setProfile(res.data.profile);
        setTransactions(res.data.transactions || []);
        setTxTotal(res.data.meta?.pagination?.total || 0);
      } else {
        toast.error(res.data.message || "Reseller not found");
        navigate("/Admin/Resellers");
      }
    } catch {
      setScreenloader(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => { fetchDetail(); }, [id, txPage]); // eslint-disable-line

  const handleAssignCredits = async (e) => {
    e.preventDefault();
    const parsed = parseInt(credits);
    if (!parsed || parsed < 1) { toast.error("Enter a valid number of credits."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/resellers/${id}/credits`,
        { credits: parsed, notes },
        { headers: { "access-token": localStorage.getItem("accessToken") } }
      );
      setSubmitting(false);
      if (res.data.status) {
        toast.success(res.data.message);
        setShowModal(false);
        setCredits("");
        setNotes("");
        fetchDetail();
      } else {
        toast.error(res.data.message || "Could not assign credits");
      }
    } catch {
      setSubmitting(false);
      toast.error("Something went wrong");
    }
  };

  const totalCredits = profile?.total_credits ?? 0;
  const availableCredits = profile?.available_credits ?? 0;
  const usedCredits = totalCredits - availableCredits;

  const statCardStyle = { borderRadius: "10px", padding: "20px", color: "#fff", marginBottom: "16px" };
  const statLabel = { fontSize: "12px", fontWeight: 600, opacity: 0.8, marginBottom: "6px", letterSpacing: "0.5px" };
  const statValue = { fontSize: "28px", fontWeight: 700, lineHeight: 1 };

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">

            {/* Header */}
            <div className="row mb-3 align-items-center">
              <div className="col">
                <h2>Reseller Detail</h2>
                {profile && (
                  <p className="text-muted mb-0">
                    {profile.fname} {profile.lname} &mdash; {profile.email}
                  </p>
                )}
              </div>
              <div className="col-auto d-flex gap-2 g-3">
                <Can permission="admin.resellers.assign_credits">
                  <Button className="admin-btn" onClick={() => setShowModal(true)}>
                    + Assign Credits
                  </Button>
                </Can>
                <Button
                  style={{ backgroundColor: "#5f75a5", color: "#fff", border: "none" }}
                  onClick={() => navigate("/Admin/Resellers")}
                >
                  Back
                </Button>
              </div>
            </div>

            {/* Profile info + reseller code */}
            {profile && (
              <Card className="mb-4">
                <Card.Body>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="row g-2">
                        {[
                          ["Email", profile.email],
                          ["Name", `${profile.fname} ${profile.lname}`],
                          ["Organization", profile.organization || "—"],
                          ["Country", profile.country || "—"],
                          ["Phone", profile.phone_number || "—"],
                          ["Ref ID", profile.user_reference_id],
                        ].map(([label, val]) => (
                          <div className="col-6" key={label}>
                            <div className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>{label}</div>
                            <div style={{ fontSize: "14px" }}>{val}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6 d-flex flex-column justify-content-center align-items-md-end gap-2">
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: "12px", fontWeight: 600 }}>RESELLER CODE</div>
                        <div style={{
                          fontFamily: "monospace",
                          fontSize: "28px",
                          fontWeight: 700,
                          letterSpacing: "5px",
                          backgroundColor: "#e2e7f1",
                          padding: "8px 20px",
                          borderRadius: "10px",
                          color: "#1e293b",
                          cursor: "pointer",
                        }}
                          onClick={() => {
                            navigator.clipboard.writeText(profile.reseller_code);
                            toast.success("Reseller Code copied!");
                          }}
                          title="Click to copy Reseller Code" >
                          <span style={{
                            fontFamily: "monospace",
                            fontSize: "28px",
                            fontWeight: 700,
                            letterSpacing: "5px",
                            color: "#1e293b",
                          }}>
                            {profile.reseller_code}
                          </span>
                          <FaCopy style={{ color: "#94a3b8", fontSize: "18px" }} />
                        </div>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: "12px", fontWeight: 600 }}>PROFILE ID <span style={{ opacity: 0.6, fontWeight: 400 }}>(use in User edit form)</span></div>
                        <div
                          style={{
                            display: "flex", alignItems: "center", gap: "10px", cursor: "pointer",
                            backgroundColor: "#f1f5f9", padding: "6px 16px", borderRadius: "8px",
                            border: "1px dashed #94a3b8",
                          }}
                          onClick={() => { navigator.clipboard.writeText(String(profile.id)); toast.success("Profile ID copied!"); }}
                          title="Click to copy"
                        >
                          <span style={{ fontFamily: "monospace", fontSize: "22px", fontWeight: 700, color: "#523ba8" }}>{profile.id}</span>
                          <FaCopy style={{ color: "#94a3b8" }} />
                        </div>
                      </div>
                      <div className="mt-1 text-muted" style={{ fontSize: "12px" }}>
                        {profile.user_count} associated user{profile.user_count !== 1 ? "s" : ""}
                        &nbsp;·&nbsp;
                        {profile.license_count} license{profile.license_count !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Credit stat cards */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#1e293b" }}>
                  <div style={statLabel}>TOTAL CREDITS ASSIGNED</div>
                  <div style={statValue}>{totalCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7 }}>1 credit = 1 day of license</div>
                </div>
              </div>
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#016641" }}>
                  <div style={statLabel}>AVAILABLE CREDITS</div>
                  <div style={statValue}>{availableCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7 }}>
                    ≈ {Math.floor(availableCredits / 365)} yr {availableCredits % 365} days
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#523ba8" }}>
                  <div style={statLabel}>CREDITS USED</div>
                  <div style={statValue}>{usedCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7 }}>across all assignments</div>
                </div>
              </div>
            </div>

            {/* Credit history table */}
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Credit History</h5>
                  <span className="text-muted" style={{ fontSize: "13px" }}>{txTotal} transaction{txTotal !== 1 ? "s" : ""}</span>
                </div>
                {transactions.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: "14px" }}>No credit transactions yet.</p>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table hover size="sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Credits</th>
                            <th>Performed By</th>
                            <th>Target User</th>
                            <th>License</th>
                            <th>Notes</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx, i) => (
                            <tr key={tx.id}>
                              <td>{txTotal - ((txPage - 1) * TX_LIMIT + i)}</td>
                              {/* <td>{(txPage - 1) * TX_LIMIT + i + 1}</td> */}
                              <td>
                                <span style={{
                                  display: "inline-block",
                                  padding: "2px 10px",
                                  borderRadius: "10px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  backgroundColor: tx.transaction_type === "assigned" ? "#e6f7ef" : "#fff0f0",
                                  color: tx.transaction_type === "assigned" ? "#016641" : "#dc3545",
                                }}>
                                  {tx.transaction_type === "assigned" ? "+ Assigned" : "− Used"}
                                </span>
                              </td>
                              <td style={{ fontWeight: 600 }}>{tx.credits.toLocaleString()}</td>
                              <td style={{ fontSize: "13px" }}>{tx.performed_by_email}</td>
                              <td style={{ fontSize: "13px" }}>{tx.target_user_email || "—"}</td>
                              <td style={{ fontSize: "13px" }}>{tx.license_reference_id || "—"}</td>
                              <td className="text-muted" style={{ fontSize: "13px" }}>{tx.notes || "—"}</td>
                              <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>{ShowTime(tx.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {Math.ceil(txTotal / TX_LIMIT) > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="text-muted" style={{ fontSize: "13px" }}>
                          Page {txPage} of {Math.ceil(txTotal / TX_LIMIT)}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={txPage === 1}
                            onClick={() => setTxPage(p => p - 1)}
                          >
                            ← Prev
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={txPage >= Math.ceil(txTotal / TX_LIMIT)}
                            onClick={() => setTxPage(p => p + 1)}
                          >
                            Next →
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

          </div>
        </div>
      </div>

      {/* Assign Credits Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Assign Credits</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleAssignCredits}>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "13px" }}>
              Current available: <strong>{availableCredits.toLocaleString()}</strong> credits
            </p>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Credits to Assign</Form.Label>
              <Form.Control
                type="number"
                min="1"
                placeholder="e.g. 365"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                required
              />
              <Form.Text className="text-muted">365 credits = 1 year of license for one user.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>
                Notes <span className="fw-normal text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. Q2 2026 top-up"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button className="admin-btn" type="submit" disabled={submitting}>
              {submitting ? "Assigning…" : "Assign Credits"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
